import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, Drawer, DBlock, DKV, Loading, cx } from "./StudioShared";
import { Plus, Pencil, Archive, ArchiveRestore, Trash2, Building2, Users, Target, ShieldCheck, ChevronRight } from "lucide-react";

const emptyForm = { name: "", purpose: "", units: "", status: "active" };
const toLines = (s) => (typeof s === "string" ? s.split("\n").map((x) => x.trim()).filter(Boolean) : (s || []));

export default function DepartmentManager() {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [list, m] = await Promise.all([api.get("/admin/departments"), api.get("/admin/departments/meta")]);
    setItems(list.data); setMeta(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openView = async (row) => {
    try { const r = await api.get(`/admin/departments/${row.id}`); setView(r.data); } catch { setView(row); }
  };
  const closeView = () => setView(null);

  const startCreate = () => setEditing({ ...emptyForm });
  const startEdit = (d) => setEditing({ id: d.id, name: d.name, purpose: d.purpose, units: (d.units || []).join("\n"), status: d.status, is_core: d.is_core });

  const saveForm = async () => {
    if (!editing.name.trim()) { toast.error("Department name is required"); return; }
    setBusy(true);
    const payload = { name: editing.name, purpose: editing.purpose, units: toLines(editing.units), status: editing.status };
    try {
      if (editing.id) { await api.patch(`/admin/departments/${editing.id}`, payload); toast.success("Department updated."); }
      else { await api.post("/admin/departments", payload); toast.success("Department created."); }
      setEditing(null); closeView(); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); closeView(); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const archive = (d) => act(() => api.post(`/admin/departments/${d.id}/archive`), "Status updated.");
  const del = (d) => { if (window.confirm(`Delete department "${d.name}"?`)) act(() => api.delete(`/admin/departments/${d.id}`), "Department deleted."); };

  if (!items || !meta) return <Loading label="Loading organization…" />;
  const c = meta.counts;

  return (
    <StudioPage title="Department Manager" testid="department-manager-page"
      question="The AIPOS organization chart — business units that own strategists, missions, workflows, and knowledge. Resources auto-organize by department."
      actions={<button onClick={startCreate} data-testid="department-new-btn" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors rounded-sm"><Plus size={15} /> New Department</button>}>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Departments" value={c.departments} testid="department-count-total" />
        <Metric label="Core" value={c.core} sub="Protected" testid="department-count-core" />
        <Metric label="Strategists" value={c.strategists} testid="department-count-strategists" />
        <Metric label="Missions" value={c.missions} testid="department-count-missions" />
      </div>

      {/* Org chart: CEO → departments */}
      <div className="mb-4 flex items-center gap-2 text-slate-300" data-testid="org-chart-root">
        <span className="inline-flex items-center gap-1.5 bg-gold/15 text-gold border border-gold/40 rounded-sm px-3 py-1.5 text-sm font-semibold"><ShieldCheck size={14} /> CEO · Executive Office</span>
        <span className="text-slate-600 text-xs">owns {c.departments} departments</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="department-grid">
        {items.map((d) => (
          <button key={d.id} onClick={() => openView(d)} data-testid={`department-card-${d.id}`}
            className={cx("text-left border rounded-sm p-4 transition-colors hover:border-gold/50", d.status === "archived" ? "border-white/5 opacity-60 bg-navy" : "border-white/10 bg-navy-800")}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 size={16} className="text-gold shrink-0" />
                <span className="text-white font-medium truncate">{d.name}</span>
              </div>
              {d.is_core ? <Badge tone="gold">core</Badge> : <Badge tone="slate">custom</Badge>}
            </div>
            <p className="text-slate-500 text-xs mt-1 line-clamp-2">{d.purpose}</p>
            <div className="flex flex-wrap gap-1 mt-3">
              {(d.units || []).slice(0, 4).map((u) => <span key={u} className="text-[10px] text-slate-400 border border-white/10 rounded-sm px-1.5 py-0.5">{u}</span>)}
              {(d.units || []).length > 4 && <span className="text-[10px] text-slate-600">+{d.units.length - 4}</span>}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-300">
              <span className="inline-flex items-center gap-1"><Users size={12} className="text-gold" /> {d.counts.strategists}</span>
              <span className="inline-flex items-center gap-1"><Target size={12} className="text-gold" /> {d.counts.missions}</span>
              <span className="ml-auto text-gold inline-flex items-center gap-0.5">Open <ChevronRight size={12} /></span>
            </div>
          </button>
        ))}
      </div>

      {/* Department dashboard drawer */}
      <Drawer open={!!view && !editing} onClose={closeView} testid="department-drawer" title={view?.name} subtitle={view?.is_core ? "Core department" : "Custom department"}>
        {view && (
          <>
            <div className="flex flex-wrap gap-2">
              {view.is_core ? <Badge tone="gold">core</Badge> : <Badge tone="slate">custom</Badge>}
              <Badge tone={view.status === "archived" ? "red" : "green"}>{view.status}</Badge>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="department-drawer-actions">
              <DBtn icon={Pencil} label="Edit" testid="department-edit-btn" onClick={() => startEdit(view)} />
              <DBtn icon={view.status === "archived" ? ArchiveRestore : Archive} label={view.status === "archived" ? "Restore" : "Archive"} testid="department-archive-btn" onClick={() => archive(view)} disabled={busy} />
              {!view.is_core && <DBtn icon={Trash2} label="Delete" danger testid="department-delete-btn" onClick={() => del(view)} disabled={busy} />}
            </div>
            {view.purpose && <DBlock title="Purpose"><p className="text-slate-300 text-sm">{view.purpose}</p></DBlock>}

            {/* Mini executive dashboard */}
            <div className="grid grid-cols-3 gap-2" data-testid="department-dashboard">
              <MiniStat label="Strategists" value={view.counts.strategists} />
              <MiniStat label="Missions" value={view.counts.missions} />
              <MiniStat label="Templates" value={view.counts.templates} />
            </div>

            <DBlock title="Sub-units">
              <div className="flex flex-wrap gap-1.5">{(view.units || []).map((u) => <Badge key={u} tone="slate">{u}</Badge>)}</div>
            </DBlock>

            <DBlock title={`Strategists (${(view.strategists || []).length})`}>
              {(view.strategists || []).length === 0 ? <p className="text-slate-500 text-sm">No strategists yet — assign one by setting its Department to "{view.name}".</p> : (
                <div className="space-y-1" data-testid="department-strategists">
                  {view.strategists.map((s) => <div key={s.id} className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-1.5 text-sm text-slate-300"><span className="truncate">{s.name}</span><Badge tone={s.type === "system" ? "gold" : "slate"}>{s.type}</Badge></div>)}
                </div>)}
            </DBlock>

            <DBlock title={`Missions (${(view.missions || []).length})`}>
              {(view.missions || []).length === 0 ? <p className="text-slate-500 text-sm">No missions yet.</p> : (
                <div className="space-y-1" data-testid="department-missions">
                  {view.missions.map((m) => <div key={m.id} className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-1.5 text-sm text-slate-300"><span className="truncate">{m.name}</span><Badge tone={m.is_template ? "gold" : "green"}>{m.is_template ? "template" : m.status}</Badge></div>)}
                </div>)}
            </DBlock>
            <DKV label="Owner" value={view.owner_name} />
          </>
        )}
      </Drawer>

      {/* Create / Edit drawer */}
      <Drawer open={!!editing} onClose={() => setEditing(null)} testid="department-form-drawer" title={editing?.id ? "Edit Department" : "New Department"} subtitle={editing?.id ? editing?.name : "Add a business unit"}>
        {editing && (
          <div className="space-y-3">
            <Field label="Name *"><input data-testid="department-form-name" value={editing.name} disabled={editing.is_core} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={cx(inp, editing.is_core && "opacity-60")} />{editing.is_core && <p className="text-[11px] text-slate-500 mt-1">Core department names are fixed (ownership matches on name).</p>}</Field>
            <Field label="Purpose"><textarea data-testid="department-form-purpose" rows={3} value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })} className={inp} /></Field>
            <Field label="Sub-units (one per line)"><textarea data-testid="department-form-units" rows={4} value={editing.units} onChange={(e) => setEditing({ ...editing, units: e.target.value })} className={inp} /></Field>
            <Field label="Status"><select data-testid="department-form-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp}><option value="active">active</option><option value="archived">archived</option></select></Field>
            <div className="flex gap-2 pt-2">
              <button onClick={saveForm} disabled={busy} data-testid="department-form-save" className="bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60 rounded-sm">{busy ? "Saving…" : editing.id ? "Save changes" : "Create department"}</button>
              <button onClick={() => setEditing(null)} className="border border-white/15 text-slate-300 px-4 py-2 text-sm hover:bg-white/5 rounded-sm">Cancel</button>
            </div>
          </div>
        )}
      </Drawer>
    </StudioPage>
  );
}

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const Field = ({ label, children }) => (<div><SectionLabel className="mb-1">{label}</SectionLabel>{children}</div>);
const MiniStat = ({ label, value }) => (<div className="bg-navy border border-white/10 rounded-sm p-3 text-center"><div className="font-heading text-xl text-gold">{value}</div><div className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">{label}</div></div>);
const DBtn = ({ icon: Icon, label, onClick, testid, danger, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={label}
    className={cx("flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm transition-colors disabled:opacity-40", danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/10 text-slate-200 hover:border-gold/50 hover:text-white")}>
    <Icon size={13} className={danger ? "" : "text-gold"} /> {label}
  </button>
);
